/**
 * SeededRng — deterministic pseudo-random generator for server-authoritative
 * mini-games (fishing, card draws, loot crates, etc.).
 *
 * Uses a simple 32-bit xorshift with string seed hashing. Not cryptographic.
 */

export class SeededRng {
  private state: number;

  constructor(seed: string) {
    this.state = this.hashString(seed);
    // Avoid zero state.
    if (this.state === 0) this.state = 0x12345678;
  }

  /** Returns a float in [0, 1). */
  next(): number {
    // Xorshift32
    this.state ^= this.state << 13;
    this.state ^= this.state >>> 17;
    this.state ^= this.state << 5;
    // Convert to 0–1 (discard sign, keep 32 bits).
    return ((this.state >>> 0) / 4294967296);
  }

  /** Returns a float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Returns an integer in [min, max] inclusive. */
  rangeInt(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** Weighted pick from a list. Returns null if all weights are 0. */
  pick<T>(items: T[], weights: number[]): T | null {
    if (items.length !== weights.length || items.length === 0) return null;
    const total = weights.reduce((a, b) => a + b, 0);
    if (total <= 0) return null;
    let roll = this.next() * total;
    for (let i = 0; i < items.length; i++) {
      roll -= weights[i];
      if (roll <= 0) return items[i];
    }
    return items[items.length - 1];
  }

  /** Returns true with probability `chance` (0–1). */
  chance(probability: number): boolean {
    return this.next() < probability;
  }

  private hashString(input: string): number {
    let h = 0;
    for (let i = 0; i < input.length; i++) {
      h = (h << 5) - h + input.charCodeAt(i);
      h |= 0;
    }
    return h >>> 0;
  }
}
